<script setup>
import VButton from '@/modules/Signup/components/VButton.vue';
import { computed, onMounted, ref } from 'vue';
import VIconRender from '@/modules/Signup/components/VIconRender.vue';
import LeftChevronIcon from '@/modules/Signup/__clients/ezi/icons/left-chevron.svg';
import RightChevronIcon from '@/modules/Signup/__clients/ezi/icons/right-chevron.svg';
import DownloadIcon from '@/modules/Signup/__clients/ezi/icons/download-speed.svg';
import UploadIcon from '@/modules/Signup/__clients/ezi/icons/upload-speed.svg';
import EziRewardsIcon from '@/modules/Signup/__clients/ezi/icons/ezi-rewards.svg';
import DescriptiveList from '@/modules/Signup/components/pricing-plans/DescriptiveList.vue';
import { useSignUpModuleStore } from '@/stores/modules/signup/signUpStore';
import { storeToRefs } from 'pinia/dist/pinia';
import { currencyFormatter } from '@/modules/Signup/utils';

const
    signUpStore = useSignUpModuleStore(),
    { serviceQualificationResponse } = storeToRefs(signUpStore);

// TODO: refactor and just use store instead, as there were changes.
// eslint-disable-next-line no-undef
const props = defineProps({
    plans: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: true,
    },
    selectedPlanId: {
        type: [String, Number],
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

// eslint-disable-next-line no-undef
const emit = defineEmits(['select-plan']);

const responsiveOptions = ref([
    {
        breakpoint: '1400px',
        numVisible: 2,
        numScroll: 1,
    },
    {
        breakpoint: '1199px',
        numVisible: 3,
        numScroll: 1,
    },
    {
        breakpoint: '767px',
        numVisible: 2,
        numScroll: 1,
    },
    {
        breakpoint: '575px',
        numVisible: 1,
        numScroll: 1,
    },
]);

const showNavigators = computed(() => props.plans?.length > 3);

const handleSelectedButton = (id) => {
    const selectedPlan = props.selectedPlanId === id;
    return {
        label: `Select${selectedPlan ? 'ed!' : ''}`,
        background: selectedPlan ?
            '!tw-bg-[linear-gradient(75.47deg,_#FF0090_10.53%,_#FF5064_86.98%)]' :
            '!tw-bg-secondary',
        border: selectedPlan ? 'tw-border-[#FF0090]' : 'tw-border-[#312C704D]',
    };
};

onMounted(() => {
    connectionTypeIdentifier();
});

// This is necessary when the sequence is affected based on conditions
const adjustedPlansSequence = computed(() => {
    if (!props.plans?.length) return [];

    const mostPopularIndex = props.plans.findIndex(plan => plan.Attributes?.MostPopular === 'true');

    // If non found, return original
    if (mostPopularIndex === -1) {
        return props.plans;
    }

    const reordered = [...props.plans];
    const [mostPopular] = reordered.splice(mostPopularIndex, 1);

    // Insert to the 2nd position
    reordered.splice(1, 0, mostPopular);

    return reordered;
});

const connectionType = ref({});

function connectionTypeIdentifier() {
    const isUpgrade = ['FTTN', 'FTTC'].includes(serviceQualificationResponse.value.ServiceType);
    const types = {
        upgrade: {
            class: 'tw-text-primary tw-bg-[#FF00901A]',
            text: 'Get a Free <strong>nbn<sup>&reg;</sup></strong> fibre upgrade',
        },
        available: {
            class: 'tw-text-secondary tw-bg-[#312C701A]',
            text: 'Available at your address',
        },
    };
    connectionType.value = isUpgrade ? types.upgrade : types.available;
}
</script>

<template>
    <!-- TODO: maximize passthrough -->
    <div class="sign-up__component-pricing-plans tw-relative tw-overflow-hidden">
        <!-- Background -->
        <div class="tw-bg-[#FF50640D] tw-w-full tw-h-[80%] tw-top-[10%] tw-left-0 tw-absolute"></div>

        <!-- Diagonal BG -->
        <div class="tw-absolute tw-top-[10%] tw-left-0 tw-w-full tw-h-[80%] tw-overflow-hidden">
            <div
                class="tw-w-[100%] tw-h-[150%] -tw-mt-28 tw-bg-[#FF5064] tw-opacity-[7%] tw-transform tw-rotate-[-25deg]
            tw-origin-top-right"
            ></div>
        </div>

        <!-- Disabled State -->
        <div
            v-if="props.disabled"
            class="tw-h-full tw-w-full tw-absolute tw-top-0 tw-left-0 tw-z-10 tw-bg-[rgba(255,255,255,0.55)]"
        ></div>

        <div class="tw-w-2/3 tw-mx-auto">
            <Carousel
                :value="adjustedPlansSequence"
                :numVisible="3"
                :numScroll="3"
                :responsiveOptions="responsiveOptions"
                :showIndicators="false"
                :showNavigators="showNavigators"
                containerClass="tw-font-raleway"
            >
                <template #previousicon>
                    <VIconRender :icon-src="LeftChevronIcon" />
                </template>
                <template #nexticon>
                    <VIconRender :icon-src="RightChevronIcon" />
                </template>

                <template #item="{ data }">
                    <div class="tw-group tw-bg-white tw-relative tw-z-10">
                        <span
                            class="tw-w-[90%] tw-mx-auto tw-h-[2rem] tw-flex tw-items-center tw-justify-center
                            tw-text-white tw-bg-[linear-gradient(90deg,_#FF0090_11.3%,_#FF5064_89.87%)]
                            tw-text-[14px]"
                            :class="{
                                'tw-visible': data.Attributes.MostPopular === 'true',
                                'tw-invisible': data.Attributes.MostPopular === 'false'
                            }"
                        >Most Popular</span>

                        <div
                            class="tw-border tw-border-solid group-hover:tw-border-[#FF0090]"
                            :class="[handleSelectedButton(data?.ID).border]"
                        >
                            <header class="tw-pt-4 tw-text-center">
                                <span class="tw-font-medium tw-text-[16px] tw-inline-block tw-mb-2">
                                    Unlimited Data
                                </span>
                                <h4 class="tw-font-neue-kabel-bold tw-uppercase !tw-text-[32px] tw-px-6">
                                    {{ data?.ExternalName }}
                                </h4>
                                <div
                                    class="tw-text-[16px] tw-py-1 tw-mt-2"
                                    :class="[connectionType.class]"
                                    v-html="connectionType.text"
                                ></div>
                            </header>

                            <div class="tw-flex tw-flex-col tw-gap-7 tw-mt-6 tw-font-raleway">
                                <!-- Section: Plan Speeds -->
                                <section class="tw-px-6">
                                    <h5 class="!tw-font-bold !tw-text-[14px] tw-text-secondary">Plan Speeds</h5>
                                    <p class="">Typical speeds during busy times (7pm to 11pm)</p>

                                    <!-- TODO: refactor this -->
                                    <div class="tw-flex tw-w-[158px] tw-mx-auto tw-bg-[#FF7D901A] tw-py-2 tw-mt-6">
                                        <div
                                            class="tw-flex-1 tw-text-center tw-border-0 tw-border-r tw-border-[#FF506426]
                                        tw-border-solid"
                                        >
                                            <span class="tw-font-medium tw-block tw-mb-2">Upload</span>
                                            <div
                                                class="tw-flex tw-justify-center tw-items-center tw-gap-x-2
                                            tw-text-primary"
                                            >
                                                <VIconRender :icon-src="UploadIcon" />
                                                <div>
                                                    <strong class="tw-text-[24px] tw-block tw-leading-none">
                                                        {{ data.properties.speed.upload }}
                                                    </strong>
                                                    <span class="tw-font-semibold">Mbps</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="tw-flex-1 tw-text-center">
                                            <span class="tw-font-medium tw-block tw-mb-2">Download</span>
                                            <div
                                                class="tw-flex tw-flex-row-reverse tw-justify-center tw-items-center
                                            tw-gap-x-2 tw-text-primary"
                                            >
                                                <VIconRender :icon-src="DownloadIcon" />
                                                <div>
                                                    <strong class="tw-text-[24px] tw-block tw-leading-none">
                                                        {{ data.properties.speed.download }}
                                                    </strong>
                                                    <span class="tw-font-semibold">Mbps</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <!-- Section: Amount -->
                                <section
                                    class="tw-text-white tw-w-max tw-text-[32px] tw-font-bold tw-px-8 tw-py-2
                                    tw-bg-[linear-gradient(65.1deg,_#FF0090_-19.09%,_#FF7D90_91.65%)]"
                                >
                                    {{ currencyFormatter(data.amount) }}<span class="tw-text-[14px]">/mth</span>
                                </section>

                                <!-- Section: Perfect for -->
                                <DescriptiveList :items="data.Attributes.PerfectFor" title="Perfect For:" />

                                <!-- Section: Ezi reward -->
                                <section
                                    class="tw-bg-[#FF00901A] tw-text-center tw-py-5 tw-px-6 tw-flex tw-flex-col tw-gap-6"
                                >
                                    <header class="tw-text-[14px] tw-font-bold">
                                    <span class="tw-flex tw-justify-center tw-gap-1">
                                        Get X <VIconRender :icon-src="EziRewardsIcon" /> Points
                                    </span>
                                        <span class="tw-block">for every dollar you spend</span>
                                    </header>

                                    <div>
                                        <!-- image here -->
                                        <div>
                                            <strong class="tw-block tw-mb-1">{{ data.Attributes.PromoTitle }}</strong>
                                            {{ data.Attributes.PromoDescription }}
                                        </div>
                                    </div>

                                    <footer class="tw-mt-4">
                                        Further information detail - <strong>additional pricing</strong>?
                                    </footer>
                                </section>

                                <!-- Section: General Service Benefits -->
                                <DescriptiveList
                                    :items="data.Attributes.Features"
                                />

                                <!-- Section: General Disclaimer -->
                                <section class="tw-px-6">
                                    <p>
                                        Further detailed disclaimer/information about nbn connection type, speed,
                                        availability etc. Further detailed disclaimer/information about nbn connection
                                        type,
                                        speed, availability etc.
                                    </p>
                                </section>

                                <a
                                    v-if="data.Attributes?.LinkToCis"
                                    :href="data.Attributes.LinkToCis"
                                    target="_blank"
                                    class="tw-text-center !tw-text-[#FF5064] !tw-underline tw-inline-block tw-mt-4
                                    tw-mb-6"
                                >
                                    Critical Information Summary<br />nbn&reg; Fact Sheet
                                </a>
                            </div>

                            <footer class="tw-flex tw-justify-between tw-items-end tw-px-6 tw-py-5">
                                <span class="tw-inline-block tw-mb-1">Pricing Includes GST</span>
                                <VButton
                                    @click="emit('select-plan', data)"
                                    :label="handleSelectedButton(data?.ID).label"
                                    :bg-class="handleSelectedButton(data?.ID).background"
                                />
                            </footer>
                        </div>
                    </div>
                </template>
            </Carousel>
        </div>
    </div>
</template>
